import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchProjects } from "../../../api/project";
import { status as statusMap } from "../../utils/statusMap";

const RECENT_KEY = "promang.projectSwitcher.recent";
const RECENT_LIMIT = 4;

const STATUS_TONE = {
  ongoing: ["#e3f5e8", "#1e7d3a"],
  ONGOING: ["#e3f5e8", "#1e7d3a"],
  active: ["#e3f5e8", "#1e7d3a"],
  planned: ["#e6effc", "#1f5fb8"],
  new: ["#e6effc", "#1f5fb8"],
  NEW: ["#e6effc", "#1f5fb8"],
  pending: ["#f1f2f4", "#5a6472"],
  pause: ["#f1f2f4", "#5a6472"],
  inactive: ["#f1f2f4", "#5a6472"],
  finished: ["#efeaf9", "#5f3fa8"],
  FINISHED: ["#efeaf9", "#5f3fa8"],
  accomplished: ["#efeaf9", "#5f3fa8"],
  ACCOMPLISHED: ["#efeaf9", "#5f3fa8"],
  closed: ["#efeaf9", "#5f3fa8"],
  CLOSED: ["#efeaf9", "#5f3fa8"],
};

const readRecent = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((row) => row?.id) : [];
  } catch {
    return [];
  }
};

const writeRecent = (rows) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(rows.slice(0, RECENT_LIMIT)));
  } catch {
    /* private mode */
  }
};

const displayTitleOf = (project) =>
  project?.displayTitle ||
  [project?.project_number, project?.name].filter(Boolean).join(" ") ||
  project?.name ||
  "";

const rememberProject = (project) => {
  if (!project?.id) return;
  const row = {
    id: project.id,
    project_number: project.project_number ?? "",
    name: project.name || "",
    displayTitle: displayTitleOf(project),
    status: typeof project.status === "string" ? project.status : project.status?.name || "",
    customerName:
      project.customerName ||
      project.customer?.displayTitle ||
      project.customer?.name ||
      "",
    category:
      project.category ||
      project.project_category?.displayTitle ||
      project.project_category?.name ||
      "",
  };
  writeRecent([row, ...readRecent().filter((item) => String(item.id) !== String(row.id))]);
};

const rowOf = (project) => ({
  id: project.id,
  no: String(project.project_number ?? project.no ?? ""),
  name: project.name || project.displayTitle || "",
  customer: project.customerName || project.customer?.displayTitle || project.customer?.name || "",
  category: project.category || project.project_category?.displayTitle || project.project_category?.name || "",
  status: typeof project.status === "string" ? project.status : "",
  raw: project,
});

const toneOf = (status) => STATUS_TONE[status] || ["#f1f2f4", "#5a6472"];

export function ProjectSwitcher({
  currentId = null,
  showRecent = true,
  variant = "popover",
  inline = false,
  onSelect,
  onDismiss,
  children,
}) {
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(inline);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState(readRecent);
  const [anchor, setAnchor] = useState(null);

  const visible = inline || open;
  const modal = variant === "modal";

  const place = useCallback(() => {
    const node = anchorRef.current;
    if (!node) return;
    setAnchor(node.getBoundingClientRect());
  }, []);

  const openPicker = useCallback(() => {
    setQuery("");
    setActive(0);
    setRecent(readRecent());
    place();
    setOpen(true);
  }, [place]);

  const closePicker = useCallback(() => {
    if (inline) {
      onDismiss?.();
      return;
    }
    setOpen(false);
  }, [inline, onDismiss]);

  const choose = useCallback(
    (project) => {
      if (!project?.id) return;
      rememberProject(project.raw || project);
      setRecent(readRecent());
      if (!inline) setOpen(false);
      onSelect?.(project.raw || project);
    },
    [inline, onSelect],
  );

  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [visible]);

  useEffect(() => {
    if (!visible || inline) return undefined;
    place();
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [visible, inline, place]);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await fetchProjects({
          query,
          per_page: 20,
          page: 1,
          fields: "id,displayTitle,name,project_number,customer,status,project_category",
        });
        if (!cancelled) setResults(Array.isArray(response?.data) ? response.data : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [visible, query]);

  const sections = useMemo(() => {
    const q = query.trim();
    const fetched = results.map(rowOf);
    if (q) {
      return fetched.length
        ? [{ label: t("project.switcher.results", { count: fetched.length }), items: fetched }]
        : [];
    }
    const recentRows = showRecent ? recent.map(rowOf) : [];
    const recentIds = new Set(recentRows.map((row) => String(row.id)));
    const rest = showRecent ? fetched.filter((row) => !recentIds.has(String(row.id))) : fetched;
    const next = [];
    if (recentRows.length) next.push({ label: t("project.switcher.recent"), items: recentRows });
    if (rest.length) {
      next.push({
        label: showRecent ? t("project.switcher.all") : t("project.switcher.projects"),
        items: rest,
      });
    }
    return next;
  }, [query, recent, results, showRecent, t]);

  const flat = sections.flatMap((section) => section.items);
  const activeIndex = Math.min(active, Math.max(flat.length - 1, 0));
  const viewportWidth = typeof window === "undefined" ? 672 : window.innerWidth;
  /* Name + number + customer. Clamped so a narrow viewport does not overflow. */
  const popoverWidth = Math.min(640, Math.max(0, viewportWidth - 32));

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(activeIndex + 1, Math.max(flat.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(activeIndex - 1, 0));
    } else if (event.key === "Enter" && flat[activeIndex]) {
      event.preventDefault();
      choose(flat[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closePicker();
    }
  };

  const panelStyle = inline
    ? undefined
    : modal
      ? {
          position: "fixed",
          top: "14vh",
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(560px, calc(100vw - 32px))",
          zIndex: 50,
        }
      : {
          position: "fixed",
          top: (anchor?.bottom ?? 80) + 8,
          left: Math.max(16, Math.min(anchor?.left ?? 16, window.innerWidth - popoverWidth - 16)),
          width: popoverWidth,
          zIndex: 50,
        };

  const panel = visible ? (
    <div
      className={inline ? "overflow-hidden rounded-xl border border-[#dfe4ec] bg-white shadow-[0_16px_40px_rgba(20,32,52,0.16),0_2px_6px_rgba(20,32,52,0.06)]" : "overflow-hidden rounded-xl border border-[#dfe4ec] bg-white shadow-[0_16px_40px_rgba(20,32,52,0.16),0_2px_6px_rgba(20,32,52,0.06)]"}
      style={panelStyle}
      role="dialog"
      aria-label={t("project.switcher.switch")}
    >
      <div className="flex items-center gap-2.5 border-b border-[#edf0f4] px-3.5 py-3">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#6b7684" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          <circle cx="7" cy="7" r="5" />
          <path d="M11 11l3.5 3.5" />
        </svg>
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t("project.switcher.search")}
          className="min-w-0 flex-1 border-none bg-transparent text-[14px] text-[#1b2430] outline-none"
        />
        <span className="rounded border border-[#e0e5ec] px-1.5 py-0.5 font-mono text-[10px] text-[#6b7684]">Esc</span>
      </div>
      <div className="max-h-[380px] overflow-y-auto p-1.5">
        {loading && flat.length === 0 ? (
          <div className="px-3 py-7 text-center text-[13px] text-[#6b7684]">{t("project.switcher.loading")}</div>
        ) : null}
        {sections.map((section) => (
          <div key={section.label}>
            <div className="px-2.5 pb-1.5 pt-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#7a8594]">
              {section.label}
            </div>
            {section.items.map((project) => {
              const index = flat.findIndex((row) => String(row.id) === String(project.id) && row === project);
              const isCurrent = currentId != null && String(project.id) === String(currentId);
              const [bg, fg] = toneOf(project.status);
              const statusLabel = statusMap[project.status]?.label;
              return (
                <button
                  key={`${section.label}-${project.id}`}
                  type="button"
                  onMouseMove={() => {
                    if (active !== index) setActive(index);
                  }}
                  onClick={() => choose(project)}
                  className="grid w-full grid-cols-[max-content_minmax(0,1fr)_auto] items-center gap-3 rounded-[7px] px-2.5 py-2 text-left"
                  style={{ background: index === activeIndex ? "#eef4fd" : "transparent" }}
                >
                  <span className="max-w-[11rem] truncate font-mono text-[12px] text-[#4a5566]">{project.no || "—"}</span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1b2430]">
                      <span className="truncate">{project.name}</span>
                      {isCurrent ? (
                        <span className="shrink-0 text-[11px] font-medium text-[#1565c0]">
                          {t("project.switcher.current")}
                        </span>
                      ) : null}
                    </span>
                    {project.customer || project.category ? (
                      <span className="block truncate text-[12px] text-[#6b7684]">
                        {[project.customer, project.category].filter(Boolean).join(" · ")}
                      </span>
                    ) : null}
                  </span>
                  {statusLabel ? (
                    <span className="rounded-[5px] px-1.5 py-0.5 text-[11px] font-medium" style={{ background: bg, color: fg }}>
                      {t(statusLabel)}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
        {!loading && flat.length === 0 ? (
          <div className="px-3 py-7 text-center text-[13px] text-[#6b7684]">
            {query.trim()
              ? t("project.switcher.empty", { query: query.trim() })
              : t("drawings.list_ui.pick_project_empty")}
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-[#edf0f4] bg-[#fafbfc] px-3.5 py-2 text-[12px] text-[#7a8594]">
        <div className="flex gap-3.5">
          <span>{t("project.switcher.navigate")}</span>
          <span>{t("project.switcher.open_key")}</span>
        </div>
        <Link to="/dashboard/projects" className="text-[#1565c0] hover:text-[#0d4a94] hover:underline" onClick={() => !inline && setOpen(false)}>
          {t("project.switcher.all_projects")}
        </Link>
      </div>
    </div>
  ) : null;

  return (
    <>
      <span ref={anchorRef} className="pv-switch-anchor inline-flex min-w-0 max-w-full">
        {typeof children === "function" ? children({ open: visible, toggle: visible && !inline ? closePicker : openPicker }) : children}
      </span>
      {visible && !inline
        ? createPortal(
            <>
              <div
                className="fixed inset-0 z-40"
                style={{ background: modal ? "rgba(20,32,52,0.28)" : "transparent" }}
                onClick={closePicker}
              />
              {panel}
            </>,
            document.body,
          )
        : inline
          ? panel
          : null}
    </>
  );
}
